import { c as _c } from "react/compiler-runtime";
import { Check, Clipboard } from "lucide-react";
import { type MouseEvent, useState } from "react";
import { getPatientLookupCopiableNameOverride } from "@/configuration/patient-lookup-copiable-name";
import { Field } from "@/src/components/cards/common";
import { COPIED_RESET_DELAY_MS } from "@/src/components/copyable-content";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";
import type { ValerieCardDetail } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import type { EhrIdentifiers } from "@/src/lib/resolve-ehr-ids";
import { cn } from "@/src/lib/shadcn-utils";
import { properCase } from "@/src/lib/string";
import { getDisplayValue } from "@/src/lib/utils/string-utils";
import { Patient } from "@/src/models/patient";
import { getPersonName, getPersonReverseName, type NameFields, PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES } from "@/src/models/person";
export const PATIENT_HEADER_FIELD_KEYS = new Set(["givenName", "familyName", "dateOfBirth", "gender", "ehrId", "mrn"]);
export type PatientIdentifierResolver = (patient: Patient) => EhrIdentifiers;
export function withResolvedEhrIdFields(fields: Field[], resolveIdentifiers: PatientIdentifierResolver): Field[] {
  return fields.flatMap(field => field.key === "ehrId" ? [{
    key: "ehrId",
    label: "EHR ID",
    comparable: false,
    section: field.section,
    getValue: (p: Patient) => resolveIdentifiers(p).ehrSystemId
  }, {
    key: "mrn",
    label: "MRN",
    comparable: false,
    section: field.section,
    getValue: (p: Patient) => resolveIdentifiers(p).mrn
  }] : [field]);
}
export function NamePart(t0) {
  const $ = _c(17);
  const {
    value,
    label,
    display: t1,
    className,
    onCopy
  } = t0;
  let t2;
  if ($[0] !== t1 || $[1] !== value) {
    t2 = t1 === undefined ? properCase(value) : t1;
    $[0] = t1;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const display = t2;
  let t3;
  if ($[3] !== className) {
    t3 = cn("cursor-pointer hover:underline", className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== onCopy || $[6] !== value) {
    t4 = event => onCopy(event, value);
    $[5] = onCopy;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== display || $[9] !== t3 || $[10] !== t4) {
    t5 = <TooltipTrigger asChild={true}><span className={t3} onClick={t4}>{display}</span></TooltipTrigger>;
    $[8] = display;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== label) {
    t6 = <TooltipContent>Copy {label}</TooltipContent>;
    $[12] = label;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <Tooltip>{t5}{t6}</Tooltip>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
export function PatientCardTitle(t0) {
  const $ = _c(33);
  const {
    patient
  } = t0;
  const {
    group
  } = useUser();
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== group) {
    t1 = p => getPatientLookupCopiableNameOverride(group, p) ?? getPersonReverseName(p);
    $[0] = group;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getFormattedName = t1;
  let t2;
  if ($[2] !== getFormattedName || $[3] !== patient) {
    t2 = getFormattedName(patient) ?? getPersonName(patient).trim();
    $[2] = getFormattedName;
    $[3] = patient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const fullName = t2;
  let t3;
  if ($[5] !== getFormattedName) {
    t3 = getFormattedName({
      givenName: "First",
      familyName: "Last"
    });
    $[5] = getFormattedName;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const nameFormat = t3;
  const {
    givenName,
    middleName,
    familyName
  } = patient;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = async function handleCopy(event, text) {
      event.stopPropagation();
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_RESET_DELAY_MS);
      } catch {}
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleCopy = t4;
  if (!fullName) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span>Name not provided</span>;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  let t5;
  if ($[9] !== givenName) {
    t5 = givenName && <NamePart value={givenName} label="first name" onCopy={handleCopy} />;
    $[9] = givenName;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== middleName) {
    t6 = middleName && <>{" "}<NamePart value={middleName} label="middle name" className="font-normal text-muted-foreground" onCopy={handleCopy} /></>;
    $[11] = middleName;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== familyName) {
    t7 = familyName && <NamePart value={familyName} label="last name" onCopy={handleCopy} />;
    $[13] = familyName;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = `Copy name (${nameFormat})`;
  let t9;
  if ($[15] !== fullName) {
    t9 = event_0 => handleCopy(event_0, fullName);
    $[15] = fullName;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== copied) {
    t10 = copied ? <Check size={14} className="inline text-green-600" /> : <Clipboard size={14} className="relative top-[-1px] inline text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />;
    $[17] = copied;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = <TooltipTrigger asChild={true}><button type="button" aria-label={t8} className="ml-1 align-baseline" onClick={t9}>{t10}</button></TooltipTrigger>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== nameFormat) {
    t12 = <TooltipContent>Copy name ({nameFormat})</TooltipContent>;
    $[23] = nameFormat;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = <Tooltip>{t11}{t12}</Tooltip>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t14 = <TooltipProvider><span className="group text-foreground">{t5}{t6}{" "}{t7}{t13}</span></TooltipProvider>;
    $[28] = t13;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
export function getPatientCardDetails(patient: Patient, identifiers: EhrIdentifiers): ValerieCardDetail[] {
  return [{
    label: "DOB",
    value: patient.dateOfBirth ? getDisplayValue(patient.dateOfBirth, "date") : null
  }, {
    label: "Gender",
    value: patient.gender && !PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES.has(patient.gender) ? patient.gender : null
  }, {
    label: "EHR ID",
    value: identifiers.ehrSystemId
  }, {
    label: "MRN",
    value: identifiers.mrn
  }];
}